import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { AuthModal } from "@/components/AuthModal";
import { useEntitlements, useProducts, formatPrice, type ProductKey } from "@/hooks/useEntitlements";
import CheckoutDialog from "./CheckoutDialog";
import PendingPaymentActions from "./PendingPaymentActions";

interface Props {
  productKey: ProductKey;
  children: ReactNode;
}

export default function PaywallGate({ productKey, children }: Props) {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isActive, isPending, isLoading } = useEntitlements();
  const { data: products } = useProducts();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const product = products?.find((p) => p.key === productKey) ?? null;

  if (authLoading || (isAuthenticated && isLoading)) {
    return (
      <div className="min-h-dscreen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && isActive(productKey)) return <>{children}</>;

  const pending = isAuthenticated && isPending(productKey);

  return (
    <div className="min-h-dscreen flex flex-col items-center justify-center px-5 py-12 pb-[calc(3rem+env(safe-area-inset-bottom,0px))]">
      <div className="w-full max-w-md text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-6">
          {pending ? <Clock className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
        </div>

        <h1 className="font-display text-2xl sm:text-3xl mb-3">
          {product?.name ?? "Conteúdo exclusivo"}
        </h1>

        {pending ? (
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Já registramos sua compra e estamos aguardando a confirmação do pagamento. Assim que
            confirmar, esta tela libera sozinha.
          </p>
        ) : (
          <p className="text-muted-foreground mb-8 leading-relaxed">{product?.description}</p>
        )}

        {product && !pending && (
          <div className="mb-8">
            <span className="font-display text-4xl text-foreground">
              {formatPrice(product.price_cents)}
            </span>
            <span className="text-muted-foreground text-sm ml-2">
              {product.billing === "subscription" ? "por mês" : "pagamento único"}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {!isAuthenticated ? (
            <Button size="lg" className="rounded-full" onClick={() => setShowAuth(true)}>
              Entrar para continuar
            </Button>
          ) : pending ? (
            <PendingPaymentActions productKey={productKey} />
          ) : (
            <Button
              size="lg"
              className="rounded-full"
              disabled={!product?.active}
              onClick={() => setCheckoutOpen(true)}
            >
              {product?.billing === "subscription" ? "Assinar agora" : "Comprar agora"}
            </Button>
          )}
          <Button variant="ghost" className="rounded-full" onClick={() => navigate("/precos")}>
            Ver todos os planos
          </Button>
          <button
            onClick={() => navigate("/app")}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center gap-2 mt-2"
          >
            <ArrowLeft className="w-4 h-4" /> Ir para o App
          </button>
        </div>
      </div>

      <CheckoutDialog product={product} open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={() => setShowAuth(false)}
        defaultMode="login"
      />
    </div>
  );
}
